import {
  BARCODELENGTH,
  computeGTINCheckDigit,
} from 'innovatint_base/common/BarcodeUtils';
import { EP_VARIABLES } from 'innovatint_base/common/EPVariables';
import { ITEM_BARCODE, ITEM_VARIABLE } from './Constants';
import { renderBarcodeSvg, stackBarcodeSvgs } from './RenderBarcodes';

type VariableValues = Record<string, string | number | null | undefined>;

interface LayoutItem {
  itemType: string;
  varCode?: string;
  type?: string;
  prefix?: string;
  width?: number;
  height?: number;
  text?: string | number;
  svg?: string;
  showText?: boolean;
  fillColor?: string | number | null;
  fillColorVariable?: string;
  [key: string]: unknown;
}

interface Layout {
  items: Record<string, LayoutItem>;
  [key: string]: unknown;
}

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const FILL_COLOR_PLACEHOLDER_REGEX = /^\[([^\]]+)\]$/;
const NEWLINE_REGEX = /\r\n|\r|\n/;
const MULTI_BARCODE_VARIABLE_CODES = new Set(
  EP_VARIABLES()
    .filter((variable) => variable.multiBarcode)
    .map((variable) => variable.code)
);

function stringify(value: unknown): string {
  return value != null ? String(value) : '';
}

export function isMultiBarcodeItem(item: LayoutItem): boolean {
  return MULTI_BARCODE_VARIABLE_CODES.has(item.varCode as string);
}

function normalizeColorValue(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string') {
    if (HEX_COLOR_REGEX.test(value)) {
      return value;
    }
    if (/^\d+$/.test(value)) {
      return `#${Number(value).toString(16).padStart(6, '0')}`;
    }
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `#${Math.floor(value).toString(16).padStart(6, '0')}`;
  }
  return null;
}

function assignFillColor(item: LayoutItem, values: VariableValues): LayoutItem {
  if (!item || item.fillColor == null) {
    return item;
  }

  if (item.fillColor === 'transparent') {
    return { ...item, fillColor: 'transparent' };
  }

  const variableCode =
    item.fillColorVariable ||
    (typeof item.fillColor === 'string'
      ? FILL_COLOR_PLACEHOLDER_REGEX.exec(item.fillColor)?.[1]
      : null);

  if (variableCode) {
    const normalized = normalizeColorValue(values?.[variableCode]);
    if (normalized) {
      return { ...item, fillColor: normalized };
    }
    return { ...item, fillColor: 'transparent' };
  }

  const normalized = normalizeColorValue(item.fillColor);
  if (normalized) {
    return { ...item, fillColor: normalized };
  }

  return item;
}

export default function assignVariables(
  layout: Layout,
  values: VariableValues
): Layout {
  return {
    ...layout,
    items: Object.fromEntries(
      Object.entries(layout.items).map(([key, item]) => {
        let data = assignItem(item, values);
        data = assignFillColor(data, values);

        if (item.itemType === 'barcode') {
          const text = stringify(data.text);
          try {
            if (isMultiBarcodeItem(item) && text) {
              const texts = text.split(NEWLINE_REGEX).filter(Boolean);
              data.svg = stackBarcodeSvgs(
                texts.map((text) => renderBarcodeSvg(item.type!, text)),
                item.width ? item.height! / item.width : 0,
                item.showText,
                texts
              );
              data.showText = false;
            } else {
              data.svg = renderBarcodeSvg(item.type!, text);
            }
          } catch (e) {
            console.log('SVG error:', e);
          }
        }

        return [key, data];
      })
    ),
  };
}

function assignItem(item: LayoutItem, values: VariableValues): LayoutItem {
  switch (item.itemType) {
    case ITEM_VARIABLE:
      return { ...item, text: values[item.varCode!] || '' };
    case ITEM_BARCODE:
      return assignBarcodeItem(item, values);
    default:
      return item;
  }
}

function assignBarcodeItem(
  item: LayoutItem,
  values: VariableValues
): LayoutItem {
  let { varCode, prefix, type } = item;
  prefix = prefix || '';
  let value = stringify(values[varCode!]);

  if (isMultiBarcodeItem(item)) {
    const text = value
      .split(NEWLINE_REGEX)
      .map((line) => assignBarcodeText(line, prefix, type!))
      .filter(Boolean)
      .join('\n');
    return { ...item, text };
  }

  return { ...item, text: assignBarcodeText(value, prefix, type!) };
}

function assignBarcodeText(
  value: string,
  prefix: string,
  type: string
): string {
  let text = ''; // return empty text if error

  // padding and check digit calculation for fixed length types
  const fixedLength = BARCODELENGTH[type as keyof typeof BARCODELENGTH];
  if (fixedLength && value) {
    if (!prefix && value.length === fixedLength) {
      // Value should already have correct check digit, let's check
      const checkDigit = computeGTINCheckDigit(
        value.substring(0, fixedLength - 1)
      );
      if (value.charAt(fixedLength - 1) === checkDigit)
        // correct check digit
        text = value;
    } else {
      // Add prefix, padding and check digit
      const padLength = fixedLength - prefix.length - 1;
      if (padLength >= value.length) {
        // value is short enough
        value = prefix + value.padStart(padLength, '0');
        const checkDigit = computeGTINCheckDigit(value);
        if (checkDigit != null) {
          text = value + checkDigit;
        }
      }
    }
  } else {
    text = prefix + value;
  }
  return text;
}
